import { IInstalledPackage, IUpgradeSchema } from 'interfaces/IUpgradeSchema.interface';
import { SFPackage, SFPackageVersion, parseVersion } from 'sf-app/sf-package';

// Names of the dependencies of `freshScript` (its top-level `dependencies`) that have a fresh script
// for the selected version (`scriptsForVersion`). These are the ones to select automatically.
// Entries without a package name are ignored. A dependency with no script in this version is not
// returned here: it is either already installed (fine) or reported by `getUnsatisfiedDependencies`.
export const getDependencyNames = (freshScript: IUpgradeSchema | undefined, scriptsForVersion: IUpgradeSchema[]): string[] => {
    const namesInVersion = new Set(scriptsForVersion.map((script) => script.packageName));
    return (freshScript?.dependencies || [])
        .map((dependency) => dependency.package)
        .filter((name): name is string => !!name && name !== freshScript?.packageName && namesInVersion.has(name));
};

// Describes the dependencies of `freshScript` that can't be satisfied, so install must be blocked.
// A dependency is satisfied when it has a fresh script for the selected version (it gets installed
// with this run), or when it is already installed in the org at a version inside its
// `versionNumber` range. Otherwise it is reported: by name when it is not installed, or by name and
// the installed vs required version when it is installed at a version outside the range.
// Entries without a package name are ignored.
export interface IUnsatisfiedDependency {
    name: string;
    // Set when the package is installed but its version is outside the required range
    installedVersion?: string;
    from?: string;
    to?: string;
}

/**
 * Returns the dependencies of `freshScript` that can't be satisfied: not installed, or installed
 * outside the `versionNumber` range. Dependencies bundled in `scriptsForVersion` are skipped.
 * The range check mirrors the backend's `areDependenciesSatisfied`.
 */
export const getUnsatisfiedDependencies = (
    freshScript: IUpgradeSchema | undefined,
    scriptsForVersion: IUpgradeSchema[],
    installedPackages: IInstalledPackage[]
): IUnsatisfiedDependency[] => {
    const namesInVersion = new Set(scriptsForVersion.map((script) => script.packageName));
    // Same approach as the backend (areDependenciesSatisfied): turn the installed name/version pairs into
    // SFPackage objects and reuse SFPackageVersion.isWithinRange for the range check.
    const pkgObjects = installedPackages.map((installed) => new SFPackage(installed.name, parseVersion(installed.version)));
    const unsatisfied: IUnsatisfiedDependency[] = [];
    (freshScript?.dependencies || []).forEach((dependency) => {
        const name = dependency.package;
        if (!name || name === freshScript?.packageName || namesInVersion.has(name)) return;
        const pkg = pkgObjects.find((installed) => installed.name === name);
        // Required package not installed at all
        if (!pkg) {
            unsatisfied.push({ name });
            return;
        }
        const { from, to } = dependency.versionNumber || {};
        if ((from || to) && !SFPackageVersion.prototype.isWithinRange(pkg, name, from, to)) {
            unsatisfied.push({ name, installedVersion: pkg.version.toString(), from, to });
        }
    });
    return unsatisfied;
};

// Returns a copy of `selection` with the dependencies of every selected package (and their
// dependencies, and so on) selected too.
export const withDependenciesSelected = (
    selection: { [key: string]: boolean },
    scriptsForVersion: IUpgradeSchema[]
): { [key: string]: boolean } => {
    const result = { ...selection };
    const queue = Object.keys(result).filter((name) => result[name]);
    while (queue.length > 0) {
        const currentName = queue.shift();
        const currentScript = scriptsForVersion.find((script) => script.packageName === currentName);
        getDependencyNames(currentScript, scriptsForVersion).forEach((dependencyName) => {
            if (!result[dependencyName]) {
                result[dependencyName] = true;
                queue.push(dependencyName);
            }
        });
    }
    return result;
};

// Currently selected packages whose fresh scripts declare a dependency on `packageName`.
export const getSelectedDependents = (
    packageName: string,
    selectedModules: { [key: string]: boolean },
    scriptsForVersion: IUpgradeSchema[]
): string[] => {
    return scriptsForVersion
        .filter((script) =>
            selectedModules[script.packageName]
            && getDependencyNames(script, scriptsForVersion).includes(packageName))
        .map((script) => script.packageName);
};

// For every selected package, the dependencies that can't be satisfied (no fresh script for the
// selected version, and not installed in the org at a version within the required range). Packages with nothing unsatisfied are left out.
export const getPackagesWithUnsatisfiedDependencies = (
    selectedModules: { [key: string]: boolean },
    scriptsForVersion: IUpgradeSchema[],
    installedPackages: IInstalledPackage[]
): { packageName: string; unsatisfied: IUnsatisfiedDependency[] }[] => {
    return scriptsForVersion
        .filter((script) => selectedModules[script.packageName])
        .map((script) => ({
            packageName: script.packageName,
            unsatisfied: getUnsatisfiedDependencies(script, scriptsForVersion, installedPackages)
        }))
        .filter((entry) => entry.unsatisfied.length > 0);
};

// User-facing message for one unmet dependency of `packageName`: either the dependency is not
// installed (and not part of this version), or it is installed at a version outside the range.
export const getUnsatisfiedDependencyMessage = (
    t: (key: string, options?: Record<string, string>) => string,
    packageName: string,
    dependency: IUnsatisfiedDependency
): string => {
    const { name, installedVersion, from, to } = dependency;
    if (installedVersion === undefined) {
        return t('welcome_dependency_not_installed', { name: packageName, dependency: name });
    }
    const requiredKey = from && to ? 'welcome_dependency_version_between'
        : from ? 'welcome_dependency_version_min'
        : 'welcome_dependency_version_max';
    const required = t(requiredKey, { from: from || '', to: to || '' });
    return t('welcome_dependency_wrong_version', { name: packageName, dependency: name, required, installed: installedVersion });
};
